import { alpha } from '@mui/material';
import { isEmpty } from 'es-toolkit/compat';

import { omitByRecursivelyInPlace } from '@common-lib';

import { interFontUrl } from '../fonts';
import { PortalThemeModel, PortalThemeOptions } from '../interfaces';

function applyOpacityToColor(
  color: string | undefined,
  opacity: number,
): string | undefined {
  if (!color) {
    return undefined;
  }

  return alpha(color, opacity);
}

export const mapThemeToPortalThemeOptions = (
  theme: PortalThemeModel | undefined,
): Partial<PortalThemeOptions> => {
  const fontFamily = theme?.font?.id
    ? `theme-${theme.font.id}, Inter, sans-serif`
    : 'Inter, sans-serif';

  return omitByRecursivelyInPlace(
    {
      typography: {
        fontFamily,
      },
      palette: {
        mode: theme?.paletteMode === 'dark' ? 'dark' : 'light',
        primary: theme?.primary
          ? {
              main: theme?.primary.main,
              contrastText: theme?.primary.contrastText,
            }
          : undefined,
        secondary: theme?.secondary
          ? {
              main: theme?.secondary.main,
              contrastText: theme?.secondary.contrastText,
            }
          : undefined,
        background: {
          default: theme?.page?.color,
          paper: theme?.paper?.color,
        },
        text: {
          primary: theme?.page?.contrastText,
          secondary: theme?.page?.secondaryContrastText,
        },
        transactionStatus: theme?.transactionStatus,
      },
      card: {
        style: theme?.card?.style,
        color: theme?.card?.color || theme?.primary?.main,
        action: applyOpacityToColor(
          theme?.card?.contrastText || theme?.primary?.contrastText,
          0.56,
        ),
        contrastText: theme?.card?.contrastText || theme?.primary?.contrastText,
        secondaryContrastText: applyOpacityToColor(
          theme?.card?.contrastText || theme?.primary?.contrastText,
          0.8,
        ),
      },
      shape: theme?.borderRadius
        ? {
            borderRadius: theme.borderRadius,
          }
        : undefined,
      page: theme?.page
        ? {
            color: theme.page.color || undefined,
            contrastText: theme.page.contrastText || undefined,
            secondaryContrastText:
              theme.page.secondaryContrastText || undefined,
          }
        : undefined,
      paper: theme?.paper
        ? {
            color: theme.paper.color || undefined,
          }
        : undefined,
      boxShadow: theme?.boxShadow || undefined,
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            '@font-face': {
              fontFamily: theme?.font?.id
                ? `theme-${theme?.font?.id}`
                : 'Inter',
              fontStyle: 'normal',
              fontDisplay: 'swap',
              src: `url(${theme?.font?.url ?? interFontUrl}) format('truetype')`,
            },
            ...(theme?.font?.id
              ? {
                  fallbacks: [
                    {
                      '@font-face': {
                        fontFamily: 'Inter',
                        fontStyle: 'normal',
                        fontDisplay: 'swap',
                        src: `url(${interFontUrl}) format('truetype')`,
                      },
                    },
                  ],
                }
              : {}),
          },
        },
      },
    },
    isEmpty,
  ) as Partial<PortalThemeOptions>;
};
